import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
} from "react";
import { useLocation } from "react-router-dom";
import { Montage, MontageVariant } from "@features/Montage/types/montage.types";
import {
  fetchMontages,
  fetchMontageVariants,
} from "@features/Montage/services/MontageService";
import { useUserPreferences } from "./UserPreferencesContext";
import { useAuth } from "@shared/context/AuthContext";
import logger from "@shared/utils/logger";

interface MontageContextType {
  montages: Montage[];
  selectedMontage: Montage | null;
  selectMontage: (montageId: string) => void;
  loading: boolean;
  refreshMontages: () => Promise<void>;
}

const MontageContext = createContext<MontageContextType>({
  montages: [],
  selectedMontage: null,
  selectMontage: () => {},
  loading: false,
  refreshMontages: async () => {},
});

export const useMontages = () => useContext(MontageContext);

const MONTAGE_REQUIRED_ROUTE_PREFIXES = [
  "/eeg/",
  "/video-eeg",
  "/visualization/3d",
];

const MONTAGE_REQUIRED_ROUTES = new Set([
  "/eeg-data-visualization",
  "/eeg-integrated",
]);

function shouldLoadMontagesForPath(pathname: string): boolean {
  return (
    MONTAGE_REQUIRED_ROUTES.has(pathname) ||
    MONTAGE_REQUIRED_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix))
  );
}

export const MontageProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { preferences } = useUserPreferences();
  const location = useLocation();
  const [montages, setMontages] = useState<Montage[]>([]);
  const [selectedId, setSelectedId] = useState<string>(
    preferences?.eegDefaults?.montage || "",
  );
  const [loading, setLoading] = useState(false);
  const shouldLoadMontages = useMemo(
    () => shouldLoadMontagesForPath(location.pathname),
    [location.pathname],
  );

  const loadMontages = async (mounted: { current: boolean }) => {
    setLoading(true);
    try {
      // Fetch base montages and variants in parallel
      const [baseMontages, variants] = await Promise.all([
        fetchMontages(),
        fetchMontageVariants().catch(() => [] as MontageVariant[]),
      ]);
      if (!mounted.current) return;

      // Merge variants as "pseudo-montage" entries so the selector can list them
      const variantPseudo: Montage[] = variants.map((v) => {
        const base = baseMontages.find(
          (m) => m.montage_id === v.base_montage_id,
        );
        return {
          montage_id: v.variant_id,
          montage_name: v.name,
          montage_type: base?.montage_type ?? "bipolar",
          channel_pairs: base?.channel_pairs ?? [],
          is_variant: true,
          variant_id: v.variant_id,
          base_montage_id: v.base_montage_id,
          excluded_channels: v.excluded_channels,
        };
      });

      const list = [...baseMontages, ...variantPseudo];
      setMontages(list);

      if (!selectedId && list.length > 0) {
        const preferredId = preferences?.eegDefaults?.montage;
        setSelectedId(preferredId || baseMontages[0]?.montage_id || "");
      }
    } catch (err) {
      logger.warn("MontageProvider: failed to load montages", err);
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (!shouldLoadMontages || !isAuthenticated || authLoading) return;
    const mounted = { current: true };
    loadMontages(mounted);
    return () => {
      mounted.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldLoadMontages,
    isAuthenticated,
    authLoading,
    preferences?.eegDefaults?.montage,
  ]);

  const refreshMontages = async () => {
    const mounted = { current: true };
    await loadMontages(mounted);
  };

  const selectedMontage = useMemo(
    () => montages.find((m) => m.montage_id === selectedId) || null,
    [montages, selectedId],
  );

  const value = useMemo(
    () => ({
      montages,
      selectedMontage,
      selectMontage: setSelectedId,
      loading,
      refreshMontages,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [montages, selectedMontage, loading],
  );

  return (
    <MontageContext.Provider value={value}>{children}</MontageContext.Provider>
  );
};

export default MontageContext;
